import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Megaphone,
  Plus,
  Search,
  Store,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";

import { Card, CardBody } from "@/components/ui/Card";
import { PageHeader } from "@/components/ui/PageHeader";
import { api } from "@/lib/api";
import clsx from "clsx";

type ProductCode = {
  id: number;
  code: string;
  name: string;
  cost_price: string | null;
  note: string | null;
  active: boolean;
  n_shops: number;
};

type CodeShop = {
  shop_id: number;
  shop_name: string;
  shop_code: string | null;
  assigned: boolean;
};

type AdAccount = {
  id: number;
  name: string;
  meta_account_id: string;
  business_name: string | null;
  status: string | null;
  note: string | null;
  display_name: string; // note fallback name
  assigned: boolean;
};

type Campaign = {
  account_id: number;
  campaign_id: string;
  campaign_name: string;
  campaign_account_name: string | null;
  action: "include" | "exclude" | null;
};

const fmtVnd = (v: string | number | null) =>
  v === null || v === undefined || v === ""
    ? "—"
    : Number(v).toLocaleString("vi-VN") + "đ";

export function ProductCodesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  const listQ = useQuery({
    queryKey: ["product-codes", search],
    queryFn: async () =>
      (
        await api.get<ProductCode[]>("/product-codes", {
          params: { q: search || undefined },
        })
      ).data,
  });

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <PageHeader
        title="Mã hàng"
        subtitle="Mỗi mã hàng gán nhiều SHOP POScake → báo cáo doanh thu / lãi lỗ sẽ cộng dồn tất cả đơn từ các shop đã chọn về 1 mã hàng."
        icon={Tag}
        actions={
          <button onClick={() => setCreating(true)} className="btn-primary">
            <Plus size={14} /> Tạo mã hàng
          </button>
        }
      />

      <Card className="mb-4">
        <CardBody>
          <div className="relative max-w-md">
            <Search
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              placeholder="Tìm theo code / tên..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-8"
            />
          </div>
        </CardBody>
      </Card>

      <Card className="overflow-x-auto">
        <table className="table-modern">
          <thead>
            <tr>
              <th>Code</th>
              <th>Tên mặt hàng</th>
              <th className="text-right">Giá vốn</th>
              <th className="text-center">Số shop gộp</th>
              <th>Ghi chú</th>
              <th className="text-center">Trạng thái</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-slate-500">
                  Đang tải...
                </td>
              </tr>
            )}
            {listQ.data?.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-slate-500">
                  Chưa có mã hàng nào. Bấm "Tạo mã hàng" để bắt đầu.
                </td>
              </tr>
            )}
            {listQ.data?.map((pc) => (
              <tr
                key={pc.id}
                className="cursor-pointer"
                onClick={() => setDetailId(pc.id)}
              >
                <td>
                  <span className="badge-brand font-mono">{pc.code}</span>
                </td>
                <td className="font-medium">{pc.name}</td>
                <td className="text-right font-mono text-slate-700">
                  {fmtVnd(pc.cost_price)}
                </td>
                <td className="text-center">
                  <span
                    className={clsx(
                      "inline-flex items-center gap-1 font-mono text-xs",
                      pc.n_shops === 0
                        ? "text-slate-400"
                        : "text-emerald-700 font-semibold"
                    )}
                  >
                    <Store size={11} />
                    {pc.n_shops}
                  </span>
                </td>
                <td className="text-xs text-slate-500 max-w-xs truncate">
                  {pc.note || "—"}
                </td>
                <td className="text-center">
                  {pc.active ? (
                    <span className="badge-success">Active</span>
                  ) : (
                    <span className="badge-neutral">Tắt</span>
                  )}
                </td>
                <td className="text-right text-xs text-brand-600">
                  Chi tiết →
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {creating && (
        <CreateDrawer
          onClose={() => setCreating(false)}
          onSaved={(newId) => {
            qc.invalidateQueries({ queryKey: ["product-codes"] });
            setCreating(false);
            setDetailId(newId); // auto-open to assign shops
          }}
        />
      )}
      {detailId && (
        <DetailDrawer
          codeId={detailId}
          codes={listQ.data ?? []}
          onClose={() => setDetailId(null)}
          onChanged={() => {
            qc.invalidateQueries({ queryKey: ["product-codes"] });
          }}
        />
      )}
    </div>
  );
}

function CreateDrawer({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (id: number) => void;
}) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [costPrice, setCostPrice] = useState("");
  const [note, setNote] = useState("");
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      return (
        await api.post<ProductCode>("/product-codes", {
          code: code.trim(),
          name: name.trim(),
          cost_price: costPrice ? Number(costPrice) : null,
          note: note.trim() || null,
          active: true,
        })
      ).data;
    },
    onSuccess: (pc) => onSaved(pc.id),
    onError: (e: any) =>
      setErrMsg(e?.response?.data?.detail ?? String(e?.message)),
  });

  return (
    <div className="fixed inset-0 z-40 bg-black/30 flex" onClick={onClose}>
      <div
        className="ml-auto bg-white h-full w-full max-w-lg p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Tạo mã hàng mới</h2>
          <button onClick={onClose} className="btn-ghost">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label">Code (mã ngắn — duy nhất)</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Vd: DEM_1"
              className="input font-mono"
            />
          </div>
          <div>
            <label className="label">Tên mặt hàng</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Vd: Đệm Tổ Ong Gel TPE 1 lớp"
              className="input"
            />
          </div>
          <div>
            <label className="label">Giá vốn (đ/SP)</label>
            <input
              type="number"
              value={costPrice}
              onChange={(e) => setCostPrice(e.target.value)}
              placeholder="Vd: 45000"
              className="input"
            />
          </div>
          <div>
            <label className="label">Ghi chú</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="input"
            />
          </div>
          {errMsg && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
              {errMsg}
            </div>
          )}
          <div className="pt-2 border-t flex gap-2">
            <button onClick={onClose} className="btn-secondary flex-1">
              Huỷ
            </button>
            <button
              onClick={() => save.mutate()}
              disabled={!code.trim() || !name.trim() || save.isPending}
              className="btn-primary flex-1"
            >
              {save.isPending ? "Đang lưu..." : "💾 Tạo + chọn shop"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailDrawer({
  codeId,
  codes,
  onClose,
  onChanged,
}: {
  codeId: number;
  codes: ProductCode[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const cur = codes.find((c) => c.id === codeId);

  const shopsQ = useQuery({
    queryKey: ["product-code-shops", codeId],
    queryFn: async () =>
      (await api.get<CodeShop[]>(`/product-codes/${codeId}/shops`)).data,
  });

  const adsQ = useQuery({
    queryKey: ["product-code-ads", codeId],
    queryFn: async () =>
      (await api.get<AdAccount[]>(`/product-codes/${codeId}/ad-accounts`)).data,
  });

  const campaignsQ = useQuery({
    queryKey: ["product-code-campaigns", codeId],
    queryFn: async () =>
      (
        await api.get<Campaign[]>(`/product-codes/${codeId}/campaigns`)
      ).data,
  });
  const [expandedAcc, setExpandedAcc] = useState<Set<number>>(new Set());
  const [campaignRules, setCampaignRules] = useState<Record<
    string,
    "include" | "exclude" | null
  > | null>(null);

  const campaignRuleKey = (accId: number, cid: string) => `${accId}::${cid}`;
  const currentCampaignAction = (accId: number, cid: string) => {
    const k = campaignRuleKey(accId, cid);
    if (campaignRules && k in campaignRules) return campaignRules[k];
    const c = (campaignsQ.data ?? []).find(
      (x) => x.account_id === accId && x.campaign_id === cid
    );
    return c?.action ?? null;
  };
  const setCampaignAction = (
    accId: number,
    cid: string,
    action: "include" | "exclude" | null
  ) => {
    setCampaignRules((prev) => {
      const base = prev ?? {};
      return { ...base, [campaignRuleKey(accId, cid)]: action };
    });
  };

  const saveCampaignsMut = useMutation({
    mutationFn: async () => {
      const rules: any[] = [];
      // Merge: existing (with action) + local changes
      const existing = campaignsQ.data ?? [];
      for (const c of existing) {
        const k = campaignRuleKey(c.account_id, c.campaign_id);
        const action =
          campaignRules && k in campaignRules
            ? campaignRules[k]
            : c.action;
        if (action === "include" || action === "exclude") {
          rules.push({
            ad_account_id: c.account_id,
            campaign_id: c.campaign_id,
            campaign_name: c.campaign_name,
            action,
          });
        }
      }
      return (
        await api.post(`/product-codes/${codeId}/assign-campaigns`, { rules })
      ).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product-code-campaigns", codeId] });
      setCampaignRules(null);
    },
  });

  // Ads spend last 7 days for this mã hàng
  const spendQ = useQuery({
    queryKey: ["product-code-ads-spend", codeId],
    queryFn: async () => {
      const to = new Date().toISOString().slice(0, 10);
      const from = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
      return (
        await api.get<{ total_spent: number; n_accounts: number; days: any[] }>(
          `/product-codes/${codeId}/ads-spend`,
          { params: { date_from: from, date_to: to } }
        )
      ).data;
    },
  });

  const [selectedAds, setSelectedAds] = useState<Set<number> | null>(null);
  const currentAds = useMemo(
    () =>
      selectedAds ??
      new Set((adsQ.data ?? []).filter((a) => a.assigned).map((a) => a.id)),
    [selectedAds, adsQ.data]
  );

  const saveAdsMut = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/product-codes/${codeId}/assign-ad-accounts`, {
          ad_account_ids: Array.from(currentAds),
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product-code-ads", codeId] });
      qc.invalidateQueries({ queryKey: ["product-code-ads-spend", codeId] });
      setSelectedAds(null);
    },
  });

  const toggleAd = (id: number) => {
    const next = new Set(currentAds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedAds(next);
  };

  const adsDirty =
    selectedAds !== null &&
    (selectedAds.size !== (adsQ.data?.filter((a) => a.assigned).length ?? 0) ||
      (adsQ.data ?? [])
        .filter((a) => a.assigned)
        .some((a) => !selectedAds.has(a.id)));

  const [selected, setSelected] = useState<Set<number> | null>(null);
  const current = useMemo(
    () =>
      selected ??
      new Set(
        (shopsQ.data ?? []).filter((s) => s.assigned).map((s) => s.shop_id)
      ),
    [selected, shopsQ.data]
  );

  const saveMut = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/product-codes/${codeId}/assign-shops`, {
          shop_ids: Array.from(current),
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["product-code-shops", codeId] });
      qc.invalidateQueries({ queryKey: ["product-codes"] });
      onChanged();
      setSelected(null);
    },
  });

  const removeMut = useMutation({
    mutationFn: async () => {
      await api.delete(`/product-codes/${codeId}`);
    },
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });

  const toggle = (id: number) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const dirty =
    selected !== null &&
    (selected.size !==
      (shopsQ.data?.filter((s) => s.assigned).length ?? 0) ||
      (shopsQ.data ?? [])
        .filter((s) => s.assigned)
        .some((s) => !selected.has(s.shop_id)));

  if (!cur) return null;

  return (
    <div className="fixed inset-0 z-40 bg-black/30 flex" onClick={onClose}>
      <div
        className="ml-auto bg-white h-full w-full max-w-lg overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="flex items-center gap-2">
              <span className="badge-brand font-mono">{cur.code}</span>
              <h2 className="text-lg font-semibold text-slate-900">
                {cur.name}
              </h2>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Giá vốn {fmtVnd(cur.cost_price)}
            </p>
          </div>
          <button onClick={onClose} className="btn-ghost">
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">
                  Chọn SHOP POScake gộp vào mã hàng này
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Tick các shop có bán mặt hàng này. Data từ tất cả các shop
                  được tick sẽ cộng dồn.
                </p>
              </div>
            </div>

            {shopsQ.isLoading && (
              <div className="text-slate-500 py-4 text-center">
                Đang tải danh sách shop...
              </div>
            )}

            <div className="space-y-1.5">
              {(shopsQ.data ?? []).map((s) => (
                <label
                  key={s.shop_id}
                  className={clsx(
                    "flex items-center gap-3 p-3 rounded-md cursor-pointer transition-colors border",
                    current.has(s.shop_id)
                      ? "bg-brand-50 border-brand-300"
                      : "border-slate-200 hover:bg-slate-50"
                  )}
                >
                  <input
                    type="checkbox"
                    checked={current.has(s.shop_id)}
                    onChange={() => toggle(s.shop_id)}
                    className="w-4 h-4"
                  />
                  <Store size={16} className="text-brand-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {s.shop_name}
                    </div>
                    {s.shop_code && (
                      <div className="text-xs text-slate-500 font-mono">
                        {s.shop_code}
                      </div>
                    )}
                  </div>
                  {current.has(s.shop_id) && (
                    <Check size={16} className="text-brand-600" />
                  )}
                </label>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t flex gap-2">
              <div className="flex-1" />
              {dirty && (
                <button
                  onClick={() => setSelected(null)}
                  className="btn-secondary text-xs"
                >
                  Reset
                </button>
              )}
              <button
                onClick={() => saveMut.mutate()}
                disabled={!dirty || saveMut.isPending}
                className="btn-primary"
              >
                {saveMut.isPending
                  ? "Đang lưu..."
                  : `💾 Lưu ${current.size} shop`}
              </button>
            </div>
          </div>

          {/* ── Section 2: Tài khoản QC (Meta ads) ── */}
          <div className="pt-6 border-t">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                  <Megaphone size={14} className="text-amber-600" />
                  Tài khoản QC Meta
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Tick các TKQC chạy ads cho mặt hàng này (từ adsmanager.nghilong.vn).
                  Chi phí sẽ tự cộng vào lãi lỗ mã hàng.
                </p>
              </div>
              {spendQ.data && spendQ.data.total_spent > 0 && (
                <div className="text-right">
                  <div className="text-[10px] text-slate-500 uppercase">
                    7 ngày qua
                  </div>
                  <div className="text-sm font-mono font-semibold text-amber-700">
                    {Math.round(spendQ.data.total_spent).toLocaleString("vi-VN")}
                    đ
                  </div>
                </div>
              )}
            </div>

            {adsQ.isLoading && (
              <div className="text-slate-500 py-3 text-center text-xs">
                Đang tải danh sách TKQC...
              </div>
            )}
            {adsQ.error && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                Lỗi: không kết nối được adspro DB. Kiểm tra ADSPRO_DB_URL.
              </div>
            )}

            <div className="space-y-1 max-h-[500px] overflow-y-auto scrollbar-thin">
              {(adsQ.data ?? []).map((a) => {
                const isAssigned = currentAds.has(a.id);
                const isExpanded = expandedAcc.has(a.id);
                const accCampaigns = (campaignsQ.data ?? []).filter(
                  (c) => c.account_id === a.id
                );
                return (
                  <div
                    key={a.id}
                    className={clsx(
                      "rounded-md border text-sm",
                      isAssigned
                        ? "bg-amber-50 border-amber-300"
                        : "border-slate-200"
                    )}
                  >
                    <div className="flex items-center gap-2 p-2">
                      <input
                        type="checkbox"
                        checked={isAssigned}
                        onChange={() => toggleAd(a.id)}
                        className="w-4 h-4 cursor-pointer"
                      />
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => toggleAd(a.id)}
                      >
                        <div className="font-medium text-slate-900 truncate">
                          {a.display_name || a.name}
                        </div>
                        <div className="text-[10px] text-slate-500 font-mono truncate">
                          {a.business_name && (
                            <span className="text-amber-700">
                              {a.business_name} ·{" "}
                            </span>
                          )}
                          {a.meta_account_id}
                        </div>
                      </div>
                      {accCampaigns.length > 0 && (
                        <button
                          onClick={() => {
                            const next = new Set(expandedAcc);
                            if (next.has(a.id)) next.delete(a.id);
                            else next.add(a.id);
                            setExpandedAcc(next);
                          }}
                          className="text-[10px] text-brand-600 hover:text-brand-800 font-medium"
                        >
                          {isExpanded ? "▲ Ẩn" : `▼ ${accCampaigns.length} CD`}
                        </button>
                      )}
                      {isAssigned && (
                        <Check size={14} className="text-amber-600" />
                      )}
                    </div>

                    {/* Expanded campaigns */}
                    {isExpanded && (
                      <div className="border-t border-amber-200/50 bg-white/60 px-2 py-1.5 space-y-1">
                        <div className="text-[10px] text-slate-500 mb-1">
                          Loại trừ campaign nào KHÔNG thuộc mã hàng này:
                        </div>
                        {accCampaigns.map((c) => {
                          const action = currentCampaignAction(
                            c.account_id,
                            c.campaign_id
                          );
                          return (
                            <div
                              key={c.campaign_id}
                              className="flex items-center gap-2 text-[11px] py-0.5"
                            >
                              <div className="flex-1 min-w-0 font-mono truncate text-slate-700">
                                {c.campaign_name}
                              </div>
                              <div className="inline-flex rounded overflow-hidden border border-slate-200 text-[9px]">
                                <button
                                  onClick={() =>
                                    setCampaignAction(
                                      c.account_id,
                                      c.campaign_id,
                                      action === "include" ? null : "include"
                                    )
                                  }
                                  className={clsx(
                                    "px-1.5 py-0.5",
                                    action === "include"
                                      ? "bg-emerald-500 text-white"
                                      : "text-slate-500 hover:bg-slate-50"
                                  )}
                                >
                                  ✓ Bao gồm
                                </button>
                                <button
                                  onClick={() =>
                                    setCampaignAction(
                                      c.account_id,
                                      c.campaign_id,
                                      action === "exclude" ? null : "exclude"
                                    )
                                  }
                                  className={clsx(
                                    "px-1.5 py-0.5 border-l border-slate-200",
                                    action === "exclude"
                                      ? "bg-red-500 text-white"
                                      : "text-slate-500 hover:bg-slate-50"
                                  )}
                                >
                                  ✕ Loại
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {campaignRules && Object.keys(campaignRules).length > 0 && (
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => setCampaignRules(null)}
                  className="btn-secondary text-xs"
                >
                  Reset rule campaign
                </button>
                <button
                  onClick={() => saveCampaignsMut.mutate()}
                  disabled={saveCampaignsMut.isPending}
                  className="btn-primary bg-brand-600 hover:bg-brand-700 text-xs"
                >
                  {saveCampaignsMut.isPending
                    ? "Đang lưu..."
                    : `💾 Lưu ${Object.keys(campaignRules).length} rule campaign`}
                </button>
              </div>
            )}

            <div className="mt-3 pt-3 border-t flex gap-2">
              <button
                onClick={() => {
                  if (
                    confirm(
                      `Xoá mã hàng "${cur.code}"? Không xoá đơn hàng / ads, chỉ bỏ mapping.`
                    )
                  )
                    removeMut.mutate();
                }}
                className="btn-danger text-xs"
              >
                <Trash2 size={12} /> Xoá mã hàng
              </button>
              <div className="flex-1" />
              {adsDirty && (
                <button
                  onClick={() => setSelectedAds(null)}
                  className="btn-secondary text-xs"
                >
                  Reset
                </button>
              )}
              <button
                onClick={() => saveAdsMut.mutate()}
                disabled={!adsDirty || saveAdsMut.isPending}
                className="btn-primary bg-amber-600 hover:bg-amber-700"
              >
                {saveAdsMut.isPending
                  ? "Đang lưu..."
                  : `💾 Lưu ${currentAds.size} TKQC`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
